'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';

/**
 * Fires page-view tracking. Renders nothing — it exists so that needing a hook for analytics
 * doesn't force the whole page it sits on to become a client component.
 *
 * NOTE: intentionally NOT using useSearchParams here — it forces the whole (static) homepage to
 * bail to client-side rendering, which empties the crawlable HTML. pathname is enough.
 */
const PageViewTracker = () => {
  const pathname = usePathname();

  useEffect(() => {
    // NOTE: this used to call linkedInEvent('8471660') on every render.
    // '8471660' is the LinkedIn *partner* id, not a conversion id — they are
    // separate numbers — so it was recording a conversion against an id that
    // doesn't identify one. It also only mattered on the homepage, since that
    // was the only place this component was mounted; now that it is global it
    // would have fired a "conversion" on every page of the site.
    //
    // The Insight tag already records page views by itself once loaded, so
    // there is nothing to call here. Add a real linkedInEvent(<conversionId>)
    // at the specific points you want to count (form submits, signup), using an
    // id from LinkedIn Campaign Manager.

    // GA4 is initialised with `send_page_view: false` (app/layout.tsx), so pageviews must be sent
    // explicitly — and nothing did, which meant GA4 recorded no page_view events at all. Since GA4
    // derives session_start/first_visit from page_view, sessions and landing-page reports were empty
    // too.
    //
    // ORDER MATTERS, and it is easy to get wrong: simply pushing onto dataLayer from here queues the
    // event *ahead* of gtag('config', …), because the config lives in an inline lazyOnload script.
    // GA4 discards events queued before its measurement ID is configured — verified: the hit was
    // queued but no /g/collect request was ever made. That same inline script defines window.gtag,
    // so waiting for window.gtag to exist guarantees config is already queued ahead of us.
    let cancelled = false;
    const send = () => {
      const gtag = (window as unknown as { gtag?: (...args: unknown[]) => void }).gtag;
      if (typeof gtag !== 'function') return false;
      gtag('event', 'page_view', {
        page_path: pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
      return true;
    };

    if (send()) return;

    // gtag.js is lazyOnload, so on a first visit it lands well after hydration. Poll briefly rather
    // than miss the pageview, and give up rather than spin forever if analytics is blocked.
    const poll = setInterval(() => {
      if (cancelled || send()) clearInterval(poll);
    }, 300);
    const giveUp = setTimeout(() => clearInterval(poll), 30000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      clearTimeout(giveUp);
    };
  }, [pathname]);

  return null;
};

export default PageViewTracker;
